import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { 
  Server, Shield, Cloud, Network, Briefcase,
  CheckCircle, ArrowRight, Monitor, Settings, Headphones,
  Camera, KeyRound, Phone as PhoneIcon, Cable,
  UserCog, Volume2, Tv, Bell
} from 'lucide-react';

// Service data
const servicesData = {
  'it-infrastructure': {
    id: 'it-infrastructure',
    icon: Server,
    title: 'IT Infrastructure Management',
    subtitle: 'Your Complete Outsourced IT Department',
    description: 'Comprehensive IT management from network design to data center optimization. We deliver enterprise-grade infrastructure management with white-glove, relationship-driven support.',
    features: [
      'Network design, implementation, and management',
      'Server administration (Windows and macOS)',
      'Virtualization (VMware, Hyper-V, KVM)',
      'Data center management and optimization',
      'Remote Monitoring and Management (RMM)',
      'Asset lifecycle management',
      '24/7 technical support and troubleshooting',
      'IT Service Management (ITSM) implementation'
    ],
    benefits: [
      { icon: Monitor, text: 'Reduced downtime' },
      { icon: Settings, text: 'Predictable costs' },
      { icon: Headphones, text: '24/7 support' },
    ]
  },
  'cybersecurity': {
    id: 'cybersecurity',
    icon: Shield,
    title: 'Cybersecurity & Compliance',
    subtitle: 'Protecting Your Digital Assets',
    description: 'Comprehensive cybersecurity services to protect your data, systems, and reputation. We help you navigate compliance complexities and maintain a secure operational environment.',
    features: [
      'Endpoint security and monitoring',
      'Threat intelligence and vulnerability assessments',
      'Firewall and intrusion prevention (IPS) management',
      'Security Information & Event Management (SIEM)',
      'HIPAA, GDPR, SOC 2, PCI-DSS compliance',
      'NIST and ISO 27001 framework alignment',
      'Incident response and breach mitigation',
      'Security awareness training'
    ],
    benefits: [
      { icon: Shield, text: 'Enterprise-grade security' },
      { icon: CheckCircle, text: 'Compliance ready' },
      { icon: Headphones, text: 'Rapid response' },
    ]
  },
  'cloud-services': {
    id: 'cloud-services',
    icon: Cloud,
    title: 'Cloud Computing & SaaS Management',
    subtitle: 'Scalable, Secure Cloud Solutions',
    description: 'Expert guidance for cloud migration, optimization, and management. We help you leverage Microsoft 365, Google Workspace, AWS, and Azure for maximum business value.',
    features: [
      'Cloud strategy and migration planning',
      'Microsoft 365 and Google Workspace administration',
      'Azure and AWS infrastructure management',
      'SaaS application integration and licensing',
      'Cloud security and identity management',
      'Cost optimization and governance',
      'Backup and disaster recovery in the cloud',
      'Multi-cloud and hybrid cloud strategies'
    ],
    benefits: [
      { icon: Cloud, text: 'Scalable infrastructure' },
      { icon: Settings, text: 'Cost optimization' },
      { icon: Shield, text: 'Cloud security' },
    ]
  },
  'business-continuity': {
    id: 'business-continuity',
    icon: Briefcase,
    title: 'Business Continuity & Disaster Recovery',
    subtitle: 'Keep Your Business Running',
    description: 'Comprehensive backup, disaster recovery, and business continuity planning to ensure your operations never stop, even in the face of unexpected events.',
    features: [
      'Backup strategy and implementation',
      'Disaster recovery planning and testing',
      'Business continuity planning (BCP)',
      'Ransomware recovery solutions',
      'Replication and high availability',
      'Cloud-based backup solutions',
      'Regular recovery testing and validation',
      'RPO and RTO optimization'
    ],
    benefits: [
      { icon: Briefcase, text: 'Minimal downtime' },
      { icon: Shield, text: 'Data protection' },
      { icon: CheckCircle, text: 'Tested recovery' },
    ]
  },
  'voip': {
    id: 'voip',
    icon: PhoneIcon,
    title: 'VoIP & Unified Communications',
    subtitle: 'Modern Communication Systems',
    description: 'Professional VoIP phone systems and unified communications that keep your team connected, productive, and professional.',
    features: [
      'VoIP phone system design and deployment',
      'Unified communications platforms',
      'Call routing and auto-attendant setup',
      'Video conferencing integration',
      'Mobile and remote worker solutions',
      'Call analytics and reporting',
      'Voicemail-to-email and SMS integration',
      'SIP trunking and carrier management'
    ],
    benefits: [
      { icon: PhoneIcon, text: 'Professional calling' },
      { icon: Settings, text: 'Easy management' },
      { icon: Monitor, text: 'Remote ready' },
    ]
  },
  'audio-visual': {
    id: 'audio-visual',
    icon: Tv,
    title: 'Audio Visual Solutions',
    subtitle: 'Professional Meeting & Presentation Systems',
    description: 'Conference room technology, digital signage, and audio-visual systems that enhance collaboration and communication.',
    features: [
      'Conference room AV system design',
      'Video conferencing solutions (Zoom, Teams, etc.)',
      'Digital signage and display systems',
      'Sound system installation',
      'Presentation equipment integration',
      'Wireless presentation systems',
      'Room scheduling displays',
      'AV equipment maintenance and support'
    ],
    benefits: [
      { icon: Tv, text: 'Professional meetings' },
      { icon: Volume2, text: 'Clear audio' },
      { icon: Monitor, text: 'Easy to use' },
    ]
  },
  'video-surveillance': {
    id: 'video-surveillance',
    icon: Camera,
    title: 'Video Surveillance Systems',
    subtitle: 'Enterprise Security Camera Solutions',
    description: 'Advanced video surveillance systems with AI-powered analytics, remote monitoring, and integration with your security infrastructure.',
    features: [
      'IP camera installation and configuration',
      'Network Video Recorder (NVR) systems',
      'Cloud-based video management',
      'AI-powered motion detection and analytics',
      'Remote viewing and monitoring',
      'Integration with access control systems',
      'Tri-State licensed installation',
      'Ongoing maintenance and support'
    ],
    benefits: [
      { icon: Camera, text: '24/7 monitoring' },
      { icon: Shield, text: 'AI analytics' },
      { icon: Monitor, text: 'Remote access' },
    ]
  },
  'access-control': {
    id: 'access-control',
    icon: KeyRound,
    title: 'Access Control Systems',
    subtitle: 'Secure Entry Management',
    description: 'Modern access control systems that provide secure, convenient entry management with detailed audit trails and remote administration.',
    features: [
      'Card reader and keypad systems',
      'Biometric access control',
      'Mobile credential and smartphone access',
      'Time and attendance integration',
      'Visitor management systems',
      'Door strike and magnetic lock installation',
      'Multi-site centralized management',
      'Integration with video surveillance'
    ],
    benefits: [
      { icon: KeyRound, text: 'Secure access' },
      { icon: CheckCircle, text: 'Audit trails' },
      { icon: Settings, text: 'Remote management' },
    ]
  },
  'intrusion-alarms': {
    id: 'intrusion-alarms',
    icon: Bell,
    title: 'Intrusion Detection & Alarms',
    subtitle: 'Professional Security Systems',
    description: 'Monitored intrusion detection systems that protect your facilities 24/7 with instant alerts and professional monitoring.',
    features: [
      'Intrusion detection system design',
      'Door and window contact sensors',
      'Motion detectors and glass break sensors',
      '24/7 professional monitoring',
      'Mobile app alerts and notifications',
      'Integration with video surveillance',
      'Tri-State licensed installation',
      'Central station monitoring services'
    ],
    benefits: [
      { icon: Bell, text: '24/7 monitoring' },
      { icon: Shield, text: 'Instant alerts' },
      { icon: Headphones, text: 'Professional response' },
    ]
  },
  'structured-cabling': {
    id: 'structured-cabling',
    icon: Cable,
    title: 'Structured Cabling & Infrastructure',
    subtitle: 'Professional Network Cabling',
    description: 'Enterprise-grade structured cabling systems that provide the foundation for your network, phones, security cameras, and access control.',
    features: [
      'Cat6/Cat6a/Cat7 data cabling',
      'Fiber optic installation',
      'Patch panel and rack organization',
      'Cable certification and testing',
      'Wireless access point cabling',
      'PoE (Power over Ethernet) design',
      'Cable tray and conduit installation',
      'Documentation and labeling'
    ],
    benefits: [
      { icon: Cable, text: 'Certified installation' },
      { icon: Network, text: 'High performance' },
      { icon: CheckCircle, text: 'Organized infrastructure' },
    ]
  },
  'vcio': {
    id: 'vcio',
    icon: UserCog,
    title: 'vCIO Services',
    subtitle: 'Strategic Technology Leadership',
    description: 'Virtual CIO services that provide executive-level technology guidance without the cost of a full-time hire.',
    features: [
      'IT strategy and roadmap development',
      'Technology budget planning',
      'Vendor management and evaluation',
      'IT policy and governance',
      'Security and compliance strategy',
      'Digital transformation planning',
      'Technology due diligence for M&A',
      'Executive reporting and metrics'
    ],
    benefits: [
      { icon: UserCog, text: 'Strategic guidance' },
      { icon: Briefcase, text: 'Budget planning' },
      { icon: Settings, text: 'Technology roadmap' },
    ]
  }
};

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];

  // If service not found, redirect to services overview
  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const ServiceIcon = service.icon;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-mimo-primary to-mimo-accent text-white py-20">
        <div className="container-custom">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to All Services
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
              <ServiceIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold font-heading">
                {service.title}
              </h1>
              <p className="text-xl text-white/90 mt-2">{service.subtitle}</p>
            </div>
          </div>
          <p className="text-lg text-white/90 max-w-3xl">
            {service.description}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom max-w-6xl">
          <div className="grid lg:grid-cols-3 gap-12">
            
            {/* What's Included */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">
                What's Included
              </h2>
              <div className="bg-slate-50 rounded-2xl p-8">
                <ul className="space-y-4">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-mimo-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Key Benefits */}
              <div className="bg-gradient-to-br from-mimo-primary to-mimo-accent rounded-2xl p-6 text-white">
                <h3 className="text-xl font-bold font-heading mb-4">Key Benefits</h3>
                <div className="space-y-3">
                  {service.benefits.map((benefit, index) => {
                    const BenefitIcon = benefit.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <BenefitIcon className="w-5 h-5" />
                        <span>{benefit.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold font-heading text-slate-900 mb-3">
                  Ready to Get Started?
                </h3>
                <p className="text-slate-600 mb-4">
                  Contact us to discuss how this service can benefit your business.
                </p>
                <Link to="/contact" className="btn-primary w-full inline-flex items-center justify-center gap-2">
                  Request Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

              {/* Contact Info */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold font-heading text-slate-900 mb-3">
                  Questions?
                </h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>Call us at <a href="tel:201-473-4443" className="text-mimo-primary font-semibold">201-473-4443</a></p>
                  <p>Email <a href="mailto:info@mimoit.com" className="text-mimo-primary font-semibold">info@mimoit.com</a></p>
                </div>
              </div>
            </div>
          </div>

          {/* Explore Other Services */}
          <div className="mt-16 pt-16 border-t border-slate-200">
            <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6 text-center">
              Explore Our Other Services
            </h2>
            <div className="flex justify-center">
              <Link to="/services" className="btn-outline inline-flex items-center gap-2">
                View All Services
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
export { servicesData };
